import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Download, Send, Loader2, CheckCircle2, Clock, AlertTriangle, MinusCircle, Flag, FileText } from 'lucide-react';
import { PaginationBar } from './PaginationBar';
import { SubmissionAuditDrawer } from './SubmissionAuditDrawer';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;

const EMAIL_STATUS_META = {
  sent:     { label: 'Sent',     cls: 'bg-green-100 text-green-700 border-green-200',    Icon: CheckCircle2 },
  queued:   { label: 'Queued',   cls: 'bg-blue-100 text-blue-700 border-blue-200',       Icon: Clock },
  failed:   { label: 'Failed',   cls: 'bg-red-100 text-red-700 border-red-200',          Icon: AlertTriangle },
  unsent:   { label: 'Unsent',   cls: 'bg-gray-100 text-gray-600 border-gray-200',       Icon: MinusCircle },
  'no-email': { label: 'No email', cls: 'bg-gray-100 text-gray-500 border-gray-200',     Icon: MinusCircle },
};

function EmailStatusBadge({ status, error }) {
  const meta = EMAIL_STATUS_META[status] || EMAIL_STATUS_META.unsent;
  const { Icon } = meta;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${meta.cls}`}
      title={status === 'failed' && error ? `Failure reason: ${error}` : meta.label}
      data-testid={`email-status-${status}`}
    >
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  );
}

export function AdminReportsTab({ reports, handleDownloadReport, handleResendReport, resendingId, formatDate }) {
  const [page, setPage] = useState(1);
  const [flaggingId, setFlaggingId] = useState(null);
  const [flaggedIds, setFlaggedIds] = useState(new Set());
  const [auditReport, setAuditReport] = useState(null);
  const totalPages = Math.max(1, Math.ceil(reports.length / PAGE_SIZE));
  const pagedReports = useMemo(
    () => reports.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [reports, page]
  );

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const handleFlagForRefund = async (report) => {
    if (!window.confirm(`Flag report for ${report.user_email || 'this user'} for refund?\nIt will appear in the Refund Queue.`)) return;
    setFlaggingId(report.id);
    try {
      const res = await fetch(`${API_URL}/api/admin/reports/${report.id}/flag-refund`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: 'Flagged from Generated Reports tab' }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Flag failed');
      setFlaggedIds(prev => new Set(prev).add(report.id));
      toast.success('Flagged for refund — see Refund Queue tab.');
    } catch (e) {
      toast.error(e.message || 'Failed to flag report');
    } finally {
      setFlaggingId(null);
    }
  };

  return (
    <Card className="mb-8">
      <CardHeader><CardTitle>Generated Reports</CardTitle></CardHeader>
      <CardContent>
        {reports.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No reports generated yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Archetypes</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Email Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedReports.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell className="text-sm">{formatDate(report.created_at)}</TableCell>
                    <TableCell>
                      <div className="font-medium">{report.user_name}</div>
                      <div className="text-xs text-gray-500">{report.user_email}</div>
                    </TableCell>
                    <TableCell>{report.company_name}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <span className="font-medium">{report.primary_archetype}</span>
                        <span className="text-gray-400"> / </span>
                        <span>{report.secondary_archetype}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        report.report_type === 'premium' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {report.report_type === 'premium' ? 'Paid' : 'Preview'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <EmailStatusBadge status={report.email_status} error={report.email_error} />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2 flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => handleDownloadReport(report.id)} data-testid={`download-report-${report.id}`} title="Download PDF">
                          <Download className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!report.user_email || resendingId === report.id}
                          onClick={() => handleResendReport(report.id, report.user_email)}
                          data-testid={`resend-report-${report.id}`}
                          title="Resend by email"
                        >
                          {resendingId === report.id
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : <Send className="h-3 w-3" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setAuditReport(report)}
                          data-testid={`view-quiz-${report.id}`}
                          title="View original quiz submission"
                        >
                          <FileText className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={flaggingId === report.id || flaggedIds.has(report.id)}
                          onClick={() => handleFlagForRefund(report)}
                          data-testid={`flag-refund-${report.id}`}
                          title={flaggedIds.has(report.id) ? 'Flagged — check Refund Queue' : 'Flag for refund'}
                          className={flaggedIds.has(report.id) ? 'border-yellow-500 text-yellow-600' : ''}
                        >
                          {flaggingId === report.id
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : <Flag className="h-3 w-3" />}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <PaginationBar page={page} totalPages={totalPages} total={reports.length}
              pageSize={PAGE_SIZE} onChange={setPage} testIdPrefix="reports-pagination" />
          </div>
        )}
      </CardContent>
      <SubmissionAuditDrawer report={auditReport} onClose={() => setAuditReport(null)} />
    </Card>
  );
}
